import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { isEqual, orderBy } from 'lodash'
import { API, graphqlOperation } from 'aws-amplify'
import * as queries from '../../graphql/queries'
import * as mutations from '../../graphql/mutations'
import { makeErrorToast, msg } from '../../utils/makeToast'
import { fetchRecapQuoteInfo } from './quotes'
import dayjs from 'dayjs'

export const fetchCurrentPurchaseOrder = createAsyncThunk(
  'purchaseOrders/fetchCurrentPurchaseOrder',
  async ({ purchaseOrderId }, { getState, rejectWithValue }) => {
    try {
      const {
        data: { getPurchaseOrder },
      } = await API.graphql(graphqlOperation(queries.getPurchaseOrder, { id: purchaseOrderId }))
      return getPurchaseOrder
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const createCurrentPurchaseOrder = createAsyncThunk(
  'purchaseOrders/createCurrentPurchaseOrder',
  async ({ purchaseOrderData, purchaseId, projectId }, { getState, dispatch, rejectWithValue }) => {
    const { auth } = getState()

    try {
      const zeroPad = (num, places) => String(num).padStart(places, '0')
      const {
        data: { getCompany: companyDetails },
      } = await API.graphql(graphqlOperation(queries.getCompany, { id: auth.userInfo.tenantId }))
      let newInvoiceNumber = 0
      let newInvoicingyear = companyDetails.invoicingYear
      if (companyDetails.purchaseOrderInvoiceCount === null) {
        newInvoiceNumber = zeroPad(1, 4)
      } else {
        newInvoiceNumber = zeroPad(parseInt(companyDetails.purchaseOrderInvoiceCount) + 1, 4)
        newInvoicingyear = dayjs().toISOString()
      }
      if (companyDetails.invoicingYear === null) {
        newInvoicingyear = dayjs().toISOString()
      } else {
        let today = dayjs().year()
        let oldInvoicingYear = dayjs(companyDetails.invoicingYear).year()
        if (today > oldInvoicingYear) {
          newInvoicingyear = dayjs().toISOString()
          newInvoiceNumber = zeroPad(1, 4)
        }
      }
      let year = dayjs(newInvoicingyear).year()

      if (
        companyDetails.purchaseOrderInvoiceCount !== newInvoiceNumber ||
        companyDetails.invoicingYear !== newInvoicingyear
      ) {
        const updatedCompanyDetails = {
          id: auth.userInfo.tenantId,
          purchaseOrderInvoiceCount: newInvoiceNumber,
          invoicingYear: newInvoicingyear,
          _version: companyDetails._version,
        }
        await API.graphql(
          graphqlOperation(mutations.updateCompany, { input: updatedCompanyDetails })
        )
      }
      const clonePurchaseOrderData = { ...purchaseOrderData, projectId }
      clonePurchaseOrderData.tenantId = auth.userInfo.tenantId
      clonePurchaseOrderData.activeFlag = true
      clonePurchaseOrderData.purchaseOrderNumber = `PO-${year}-${newInvoiceNumber}`

      const {
        data: { createPurchaseOrder: newPurchaseOrder },
      } = await API.graphql(
        graphqlOperation(mutations.createPurchaseOrder, { input: clonePurchaseOrderData })
      )
      const {
        data: { getPurchase: purchaseItem },
      } = await API.graphql(
        graphqlOperation(queries.getPurchase, {
          id: purchaseId,
        })
      )
      const updatePurchase = {
        id: purchaseId,
        _version: purchaseItem._version,
        poAvailable: true,
        purchaseOrderId: purchaseOrderData.id,
      }
      await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatePurchase }))

      return newPurchaseOrder
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const updateCurrentPurchaseOrder = createAsyncThunk(
  'purchaseOrders/updateCurrentPurchaseOrder',
  async ({ purchaseOrderId, purchaseOrderData, projectId }, { getState, rejectWithValue }) => {
    try {
      const {
        data: { getPurchaseOrder },
      } = await API.graphql(graphqlOperation(queries.getPurchaseOrder, { id: purchaseOrderId }))
      const updatePO = {
        id: purchaseOrderId,
        _version: getPurchaseOrder._version,
      }
      for (const property in purchaseOrderData) {
        if (!isEqual(purchaseOrderData[property], getPurchaseOrder[property])) {
          updatePO[property] = purchaseOrderData[property]
        }
      }
      if (getPurchaseOrder.projectId !== projectId) {
        updatePO.projectId = projectId
      }
      const {
        data: { updatePurchaseOrder },
      } = await API.graphql(graphqlOperation(mutations.updatePurchaseOrder, { input: updatePO }))

      return updatePurchaseOrder
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const purchaseOrders = createSlice({
  name: 'purchaseOrders',
  initialState: {
    purchaseOrdersData: [],
    fetchCurrentPurchaseOrderApiStatus: 'idle',
    createOrUpdateCurrentPurchaseOrderApiStatus: 'idle',
    deleteClientApiStatus: 'idle',
    pdfModalOpenStatus: false,
    openPdfModal: false,
    purchaseTotalModal: false,
    currentPurchaseOrders: null,
    error: null,
  },
  reducers: {
    setCurrentPurchaseOrders: (state, action) => {
      state.currentPurchaseOrders = action.payload
    },
    setpdfModalOpenStatus: (state, action) => {
      state.pdfModalOpenStatus = action.payload.status
    },
    setClearCurrentPurchaseOrders: (state, action) => {
      state.currentPurchaseOrders = null
    },
    setOpenPdfModal: (state, action) => {
      state.openPdfModal = action.payload
    },
    setPurchaseTotalModal: (state, action) => {
      state.purchaseTotalModal = action.payload
    },
  },
  extraReducers: {
    [fetchCurrentPurchaseOrder.pending]: (state, action) => {
      state.fetchCurrentPurchaseOrderApiStatus = 'loading'
    },
    [fetchCurrentPurchaseOrder.fulfilled]: (state, action) => {
      state.fetchCurrentPurchaseOrderApiStatus = 'succeeded'
      state.currentPurchaseOrders = action.payload
    },
    [fetchCurrentPurchaseOrder.rejected]: (state, action) => {
      state.fetchCurrentPurchaseOrderApiStatus = 'failed'
      state.error = action.error.message
    },
    [createCurrentPurchaseOrder.pending]: (state, action) => {
      state.createOrUpdateCurrentPurchaseOrderApiStatus = 'loading'
      state.currentPurchaseOrders = action.meta.arg.purchaseOrderData
    },
    [createCurrentPurchaseOrder.fulfilled]: (state, action) => {
      state.createOrUpdateCurrentPurchaseOrderApiStatus = 'succeeded'
      state.currentPurchaseOrders = action.payload
    },
    [createCurrentPurchaseOrder.rejected]: (state, action) => {
      state.createOrUpdateCurrentPurchaseOrderApiStatus = 'failed'
      state.error = action.error.message
    },
    [updateCurrentPurchaseOrder.pending]: (state, action) => {
      state.createOrUpdateCurrentPurchaseOrderApiStatus = 'loading'
      state.currentPurchaseOrders = action.meta.arg.purchaseOrderData
    },
    [updateCurrentPurchaseOrder.fulfilled]: (state, action) => {
      state.createOrUpdateCurrentPurchaseOrderApiStatus = 'succeeded'
      state.currentPurchaseOrders = action.payload
    },
    [updateCurrentPurchaseOrder.rejected]: (state, action) => {
      state.createOrUpdateCurrentPurchaseOrderApiStatus = 'failed'
      state.error = action.error.message
    },
  },
})

export const {
  setCurrentPurchaseOrders,
  setClearCurrentPurchaseOrders,
  setpdfModalOpenStatus,
  setOpenPdfModal,
  setPurchaseTotalModal,
} = purchaseOrders.actions

export default purchaseOrders.reducer
